import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  divide as _divide,
  find as _find,
  flatMap as _flatMap,
  get as _get,
  round as _round,
  uniqBy as _uniqBy
} from 'lodash';

import {
  PolicySelectors as policyQuery,
  PolicySelectors,
  PolicySummarySelectors
} from '@amfam/policy/data-access';
import {
  CustomerInfo,
  NewVehicle,
  PremiumEstimate,
  QuickQuoteBase,
  QuickQuoteEmail,
  QuickQuoteRequest,
  RiskModel
} from '@amfam/policy/models';
import { getQuickQuotePremium } from '@amfam/policy/replace-vehicle/data-access';
import { vehicleQuery, VehicleSelectCriteria } from '@amfam/policy/vehicle/data-access';
import { userQuery } from '@amfam/shared/user';
import { Agent, agentQuery } from '@amfam/shared/utility/agent';
import { BrandSelectors } from '@amfam/shared/utility/brand';

import { ReplaceVehicleFeatureState } from '..';

const getReplaceVehicleFeatureState =
  createFeatureSelector<ReplaceVehicleFeatureState>('replace-vehicle-feature');

const getQuickQuoteFeatureState = createSelector(
  getReplaceVehicleFeatureState,
  state => state.quickQuote
);

export const getReplaceVehicleId = createSelector(getQuickQuoteFeatureState, state =>
  state ? state.vehicleId : null
);

export const getVehicleToReplace = createSelector(
  PolicySelectors.selectAutoPolicyRisks,
  getReplaceVehicleId,
  (vehicles: RiskModel[], id: string): RiskModel =>
    id && vehicles ? vehicles.find(vehicle => vehicle.vehicleId === id) : null
);

export const getExistingVehicleRiskDescription = createSelector(getVehicleToReplace, vehicle =>
  vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : ''
);

export const getQuickQuoteParams = createSelector(
  getVehicleToReplace,
  vehicleQuery.selectCriteria,
  (vehicleToReplace: RiskModel, newVehicle: VehicleSelectCriteria): QuickQuoteRequest => {
    const effectiveDate = new Date();
    effectiveDate.setDate(effectiveDate.getDate() + 1);
    return vehicleToReplace && newVehicle
      ? {
          replaceVIN: vehicleToReplace.vin,
          newVehicle: { ...newVehicle, vehicleType: 'auto' },
          policyNumber: vehicleToReplace.policyNumber,
          effectiveDate: effectiveDate,
          copyTPIIndicator: newVehicle.copyTPIIndicator === 'yes' ? true : false,
          underwritingQuestions: newVehicle.underWritingQuestions
        }
      : null;
  }
);

export const getQuickQuoteBaseInfo = createSelector(
  getVehicleToReplace,
  vehicleQuery.selectNewVehicle,
  agentQuery.getAgents,
  BrandSelectors.selectPartnerId,
  BrandSelectors.selectBrandId,
  userQuery.selectCustomerInfo,
  vehicleQuery.selectCriteria,
  (
    vehicleToReplace: RiskModel,
    newVehicle: NewVehicle,
    agents: Agent[],
    partnerId: string,
    brandId: string,
    customerInfo: CustomerInfo,
    criteria
  ): QuickQuoteBase =>
    vehicleToReplace && customerInfo
      ? {
          partnerId,
          brandId,
          producer: getProducer(
            agents,
            _get(vehicleToReplace, 'policy.assignedProducer.producerIdNum', '')
          ),
          policyNumber: vehicleToReplace.policyNumber,
          customer: customerInfo,
          existingVehicle: {
            id: vehicleToReplace.id,
            vin: vehicleToReplace.vin,
            year: vehicleToReplace.year,
            make: vehicleToReplace.make,
            model: vehicleToReplace.model,
            series: '', // TODO: Check if the series data is available
            totalPremium: _get(vehicleToReplace, 'policy.totalPremium'),
            term: _get(vehicleToReplace, 'policy.termDescription'),
            monthlyPremium: _round(
              _divide(
                _get(vehicleToReplace, 'policy.totalPremium'),
                +_get(vehicleToReplace, 'policy.termDescription').split(' ')[0]
              ),
              2
            )
          },
          newVehicle: newVehicle && {
            year: newVehicle.year,
            make: newVehicle.make,
            model: newVehicle.model,
            series: newVehicle.series,
            vin: criteria?.vin,
            msrp: newVehicle?.msrp,
            estimatedVehiclePrice: newVehicle?.estimatedValue
          }
        }
      : null
);

export const getQuickQuoteEmailInfo = createSelector(
  getQuickQuoteBaseInfo,
  getQuickQuotePremium,
  (quickQuoteBase: QuickQuoteBase, premium: PremiumEstimate): QuickQuoteEmail =>
    quickQuoteBase && premium
      ? {
          ...quickQuoteBase,
          newVehicle: {
            ...quickQuoteBase.newVehicle,
            totalPremium: premium.totalPremium,
            term: premium.termMonths,
            monthlyPremium: premium.monthlyPremium
          },
          workOrderNumber: premium.workOrderNumber
        }
      : null
);

export const getPolicyNumberForVehicle = createSelector(getVehicleToReplace, vehicle =>
  vehicle ? vehicle.policyNumber : null
);

export const getReplaceVehicleRisks = createSelector(
  PolicySelectors.selectAutoPolicyRisks,
  PolicySummarySelectors.selectPolicySummaries,
  (risks, policySummaries) => {
    const rpvRisks: Array<RiskModel> = new Array<RiskModel>();
    risks.forEach(risk => {
      policySummaries.forEach(policy => {
        if (_get(risk, 'policyNumber', '') === _get(policy, 'policyNumber', '')) {
          risk = Object.assign({}, risk, { contractState: policy.contractState });
          rpvRisks.push(risk);
        }
      });
    });
    return rpvRisks;
  }
);

function getProducer(agents: Agent[], agentId: string): Agent {
  return agents.find(agent => agent.agentId === agentId);
}

export const selectContext = createSelector(getQuickQuoteFeatureState, state =>
  state ? state.context : null
);

export const getOperation = createSelector(getQuickQuoteFeatureState, state =>
  state ? state.operation : null
);

export const getTpisInSelectedVehicle = createSelector(getVehicleToReplace, vehicle =>
  vehicle?.vehicleRoles?.filter(roles => roles.roleType === 'ThirdPartyInterest')
);

export const getQuoteToBind = createSelector(getQuickQuoteFeatureState, state => state.quoteToBind);

export const getRemainingVehicles = createSelector(
  getReplaceVehicleRisks,
  getReplaceVehicleId,
  (risks, replaceVehicleId) =>
    risks
      .filter(r => r.id !== replaceVehicleId)
      .map(risk => `${risk.year || ''} ${risk.make || ''} ${risk.model || ''}`)
);

export const getSelectedPolicy = createSelector(
  PolicySelectors.selectAutoPolicies,
  getPolicyNumberForVehicle,
  (policies, policyNumber) => policies.find(policy => policy.policyNumber === policyNumber)
);

export const getTpisInSelectedPolicy = createSelector(getSelectedPolicy, policy =>
  _uniqBy(
    _flatMap(policy.vehicles, vehicle =>
      vehicle.vehicleRoles?.filter(roles => roles.roleType === 'ThirdPartyInterest')
    ),
    'name'
  )
);

export const getSelectedVehicle = createSelector(
  getSelectedPolicy,
  getReplaceVehicleId,
  (policy, vehicleId) => policy.vehicles.find(v => v.vehicleId === vehicleId)
);

export const getSelectedPolicyDiscounts = createSelector(
  getSelectedPolicy,
  policy => policy.policyDiscounts
);

export const getReplacedVehicleDiscounts = createSelector(
  getVehicleToReplace,
  vehicle => vehicle.vehicleDiscounts
);

export const getNewVehicleDiscounts = createSelector(
  getSelectedPolicyDiscounts,
  getReplacedVehicleDiscounts,
  (policyDiscounts, vehicleDiscounts) => policyDiscounts.concat(vehicleDiscounts)
);
/**
 * Active Agent selectors
 */
export const getAllActiveAgents = createSelector(
  agentQuery.getRealAgents,
  policyQuery.selectPolicies,
  (agents, policies) => {
    return agents.filter(
      agent => agent && _find(policies, p => p.assignedProducer.producerIdNum === agent.id)
    );
  }
);

export const quickQuoteSelectors = {
  getQuickQuoteFeatureState,
  getReplaceVehicleId,
  getQuickQuoteParams,
  getVehicleToReplace,
  getQuickQuoteEmailInfo,
  getQuickQuoteBaseInfo,
  getPolicyNumberForVehicle,
  getReplaceVehicleRisks,
  selectContext,
  getOperation,
  getTpisInSelectedVehicle,
  getExistingVehicleRiskDescription,
  getRemainingVehicles,
  getSelectedPolicy,
  getSelectedPolicyDiscounts,
  getReplacedVehicleDiscounts,
  getNewVehicleDiscounts,
  getSelectedVehicle,
  getTpisInSelectedPolicy,
  getAllActiveAgents
};
